import { getMcActiveOrgId, mcGetCloudItem, mcSetCloudItemImmediate, mcSubscribeCloudItem } from './mcCloudSync';
import { idbGetJson, idbSetJson, idbListItems } from './mcStorageEngine';
import { withInventoryDeadline } from './mcInventoryOperation';

export const MC_STORAGE_LEDGER_EVENT = 'mc-storage-ledger-changed';
const KEY_PREFIX = 'mc-storage-movement-ledger:v1';
const HEAD_KEY_PREFIX = 'mc-storage-movement-head:v2';
const REVISION_KEY_PREFIX = 'mc-storage-movement-revision:v2';
const SYNC_KEY_PREFIX = 'mc-storage-sync-activity:v1';
const MAX_ENTRIES = 250;
const MAX_SYNC_ENTRIES = 500;
const MAX_CLOUD_LEDGER_BYTES = 700000; // stay safely below Firestore's 1 MiB document limit

async function readLedgerCache(read, fallback, options = {}) {
  options.assertActive?.();
  const value = options.importAction
    ? await withInventoryDeadline(read, 'Reading the revision device cache', 1000).catch(() => fallback)
    : await read().catch(() => fallback);
  options.assertActive?.();
  return value;
}

async function writeLedgerCache(key, value, metadata, options = {}) {
  options.assertActive?.();
  const task = idbSetJson(key, value, metadata).catch(() => false);
  // The server revision/sync record is authoritative. A blocked browser cache
  // must not gate its write or turn its acknowledgement into a timeout.
  if (options.importAction) return;
  await task;
}

export const MC_STORAGE_REVISION_READY_EVENT = 'mc-storage-revision-ready';

function normalizeScope(scope = 'ALL') {
  const raw = String(scope || 'ALL').trim();
  if (!raw || raw.toUpperCase() === 'ALL') return 'ALL';
  return raw.replace(/^inventory_/i, '').replace(/_Store$/i, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'ALL';
}

function orgId() {
  return String(getMcActiveOrgId?.() || 'local').trim() || 'local';
}

function cloudKey(scope = 'ALL') {
  return `${KEY_PREFIX}:${normalizeScope(scope)}`;
}

function localKey(scope = 'ALL') {
  return `${KEY_PREFIX}:${orgId()}::${normalizeScope(scope)}`;
}

function headCloudKey(scope = 'ALL') {
  return `${HEAD_KEY_PREFIX}:${normalizeScope(scope)}`;
}

function revisionCloudKey(scope = 'ALL', revision = 0) {
  return `${REVISION_KEY_PREFIX}:${normalizeScope(scope)}:r${Math.max(0, Number(revision || 0))}`;
}

function syncCloudKey(scope = 'ALL') {
  return `${SYNC_KEY_PREFIX}:${normalizeScope(scope)}`;
}

function syncLocalKey(scope = 'ALL') {
  return `${SYNC_KEY_PREFIX}:${orgId()}::${normalizeScope(scope)}`;
}


function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function productIdentity(row = {}) {
  const sku = String(row.sku || row.SKU || row.barcode || row.code || '').trim().toLowerCase();
  if (sku) return `sku:${sku}`;
  const product = String(row.product || row.productName || row.name || row.description || '').trim().toLowerCase();
  const category = String(row.category || row.sourceCategory || '').trim().toLowerCase();
  return product ? `product:${product}::${category}` : '';
}

function comparableRows(snapshot = {}) {
  const rows = Array.isArray(snapshot.preparedRows) && snapshot.preparedRows.length
    ? snapshot.preparedRows
    : Array.isArray(snapshot.rawRows) ? snapshot.rawRows : [];
  const result = new Map();
  rows.forEach((row) => {
    const key = productIdentity(row);
    if (!key) return;
    const text = (...values) => String(values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '').trim();
    result.set(key, {
      key,
      product: text(row.product, row.productName, row.name, row.description, row.product_name),
      sku: text(row.sku, row.SKU, row.barcode, row.code, row.variant_sku),
      quantity: numberValue(row.systemSoh ?? row.system_soh ?? row.realSoh ?? row.real_soh ?? row.soh ?? row.quantity ?? row.stockOnHand ?? row.inventory),
      cost: numberValue(row.cost ?? row.effectiveCost ?? row.sourceCost ?? row.unitCost ?? row.supply_price ?? row.price),
      unit: text(row.unit, row.unitName, row.uom, row.measurementUnit, row.measurement_unit),
      multiplier: numberValue(row.multiplier ?? row.unitMultiplier ?? row.unit_multiplier ?? row.conversion ?? row.conversionFactor),
      category: text(row.category, row.sourceCategory, row.product_category),
      barcode: text(row.barcode, row.ean, row.upc),
      supplier: text(row.supplier, row.vendor, row.brand),
      description: text(row.description, row.productDescription, row.product_description),
      tags: Array.isArray(row.tags) ? row.tags.join(', ') : text(row.tags, row.tag),
    });
  });
  return result;
}

function compactProduct(row = {}) {
  return { key: row.key || '', product: row.product || 'Unnamed product', sku: row.sku || '' };
}

function valueChanged(before, after) {
  if (typeof before === 'number' || typeof after === 'number') return Math.abs(numberValue(after) - numberValue(before)) > 0.000001;
  return String(before ?? '').trim() !== String(after ?? '').trim();
}

function changeRecord(type, row, fields = []) {
  return { type, ...compactProduct(row), fields };
}

function latestCanonicalSoh(entries = []) {
  // DS1.19: a site revision must compare a SKU against that same site's last
  // observed SOH, not against whichever raw snapshot/mapped column happens to
  // be available in the browser. This is especially important when selected-
  // site imports and Global Distribution revisions are interleaved.
  //
  // Lifecycle presence is deliberately ignored here. A filtered/minimum CSV can
  // omit a valid SKU for one or more imports; omission is not stock movement.
  // We therefore retain the last numeric SOH until the SKU is observed again.
  const state = new Map();
  const rows = (Array.isArray(entries) ? entries : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => (Date.parse(a.timestamp || a.createdAt || '') || 0) - (Date.parse(b.timestamp || b.createdAt || '') || 0));

  const sohField = (item = {}) => (Array.isArray(item?.fields) ? item.fields : [])
    .find((field) => String(field?.field || '').trim().toLowerCase() === 'soh');
  const numeric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  };
  const applyObserved = (item = {}, prefer = 'after') => {
    const key = String(item?.key || '').trim().toLowerCase();
    if (!key) return;
    const field = sohField(item);
    if (!field) return;
    const preferred = numeric(field?.[prefer]);
    const fallback = numeric(prefer === 'after' ? field?.before : field?.after);
    const value = preferred !== null ? preferred : fallback;
    if (value !== null) state.set(key, value);
  };

  rows.forEach((entry) => {
    // DS1.21+: once a revision carries an explicit canonical SOH checkpoint, it
    // becomes the strongest quantity authority for everything observed up to
    // that revision. The checkpoint is deliberately merged (not replaced) so
    // filtered/minimum CSV imports cannot erase SKUs that were simply omitted.
    const checkpoint = entry?.metadata?.canonicalSohState || entry?.canonicalSohState || null;
    if (checkpoint && typeof checkpoint === 'object' && !Array.isArray(checkpoint)) {
      Object.entries(checkpoint).forEach(([key, value]) => {
        const numericValue = numeric(value);
        const normalizedKey = String(key || '').trim().toLowerCase();
        if (normalizedKey && numericValue !== null) state.set(normalizedKey, numericValue);
      });
    }

    const changes = entry?.delta?.changes || {};
    (Array.isArray(changes.stock) ? changes.stock : []).forEach((item) => applyObserved(item, 'after'));

    // IMPORTANT: a CSV `added` row is not automatically a stock movement. In a
    // filtered/minimum export the same SKU may disappear for one import and then
    // reappear later. Older ledgers encoded that as removed/added and, until now,
    // the numeric SOH on the added row overwrote the SKU's prior canonical state.
    // That is exactly how a product such as Blueberry could incorrectly become
    // `20` in the continuity map without Morpheus recording the real 1 -> 20
    // movement. Use an added row only to establish the first-ever numeric baseline;
    // once a SKU already has canonical history, reappearance is presence-only.
    (Array.isArray(changes.added) ? changes.added : []).forEach((item) => {
      const key = String(item?.key || '').trim().toLowerCase();
      if (!key || state.has(key)) return;
      applyObserved(item, 'after');
    });

    // Historical `removed` rows are presence-only for the same reason. Do not
    // change canonical SOH from them; the last numeric observation must survive
    // until the SKU is actually observed again.
  });

  return state;
}

function canonicalSohCheckpoint(priorEntries = [], nextSnapshot = {}) {
  const state = latestCanonicalSoh(priorEntries);
  const current = comparableRows(nextSnapshot || {});
  current.forEach((row, key) => {
    if (!key) return;
    const quantity = Number(row?.quantity);
    if (Number.isFinite(quantity)) state.set(String(key).trim().toLowerCase(), quantity);
  });
  return Object.fromEntries(state.entries());
}

function buildRevisionDelta(previousSnapshot = null, nextSnapshot = {}, priorEntries = []) {
  const previous = comparableRows(previousSnapshot || {});
  const current = comparableRows(nextSnapshot || {});
  const canonicalSoh = latestCanonicalSoh(priorEntries);
  const initial = previous.size === 0 && canonicalSoh.size === 0;
  const changes = { added: [], removed: [], stock: [], cost: [], configuration: [], metadata: [] };
  let unchangedCount = 0;
  let netSohChange = 0;

  current.forEach((row, key) => {
    const snapshotBefore = previous.get(key);
    const canonicalQuantity = canonicalSoh.get(key);
    // Canonical per-site continuity wins for SOH. Keep the previous snapshot's
    // metadata/configuration as the comparison baseline when available, but
    // never let a stale/wrong site column poison the quantity comparison.
    const before = canonicalQuantity !== undefined
      ? { ...(snapshotBefore || row), quantity: canonicalQuantity }
      : snapshotBefore;

    if (!before) {
      changes.added.push(changeRecord('added', row, [
        { field: 'Product', before: null, after: row.product },
        { field: 'SOH', before: null, after: row.quantity, difference: row.quantity },
      ]));
      netSohChange += row.quantity;
      return;
    }

    const stockFields = [];
    const costFields = [];
    const configurationFields = [];
    const metadataFields = [];

    if (valueChanged(before.quantity, row.quantity)) {
      stockFields.push({ field: 'SOH', before: before.quantity, after: row.quantity, difference: row.quantity - before.quantity });
      netSohChange += row.quantity - before.quantity;
    }
    if (valueChanged(before.cost, row.cost)) costFields.push({ field: 'Cost', before: before.cost, after: row.cost, difference: row.cost - before.cost });
    [['Unit', 'unit'], ['Multiplier', 'multiplier']].forEach(([label, field]) => {
      if (valueChanged(before[field], row[field])) configurationFields.push({ field: label, before: before[field], after: row[field] });
    });
    [['Category', 'category'], ['Barcode', 'barcode'], ['Supplier', 'supplier'], ['Description', 'description'], ['Tags', 'tags'], ['SKU', 'sku']].forEach(([label, field]) => {
      if (valueChanged(before[field], row[field])) metadataFields.push({ field: label, before: before[field], after: row[field] });
    });

    if (stockFields.length) changes.stock.push(changeRecord('stock', row, stockFields));
    if (costFields.length) changes.cost.push(changeRecord('cost', row, costFields));
    if (configurationFields.length) changes.configuration.push(changeRecord('configuration', row, configurationFields));
    if (metadataFields.length) changes.metadata.push(changeRecord('metadata', row, metadataFields));
    if (!stockFields.length && !costFields.length && !configurationFields.length && !metadataFields.length) unchangedCount += 1;
  });

  // DS1.19: absence from a CSV is not a removal signal. Morpheus supports
  // minimum/filtered POS exports, so a missing row must simply disappear from the
  // current active snapshot while its last observed SOH remains available for the
  // next time that SKU is present. True product lifecycle removal must come from
  // an explicit authoritative lifecycle source, not from CSV row absence.

  const addedCount = changes.added.length;
  const removedCount = changes.removed.length;
  const quantityChangedCount = changes.stock.length;
  const priceChangedCount = changes.cost.length;
  const configurationChangedCount = changes.configuration.length;
  const metadataChangedCount = changes.metadata.length;
  const changedProducts = new Set(Object.values(changes).flat().map((item) => item.key).filter(Boolean));
  const changedCount = changedProducts.size;

  let summary = 'No inventory changes';
  if (initial) summary = `Initial snapshot · ${current.size} products`;
  else {
    const parts = [];
    if (addedCount) parts.push(`${addedCount} added`);
    if (removedCount) parts.push(`${removedCount} removed`);
    if (quantityChangedCount) parts.push(`${quantityChangedCount} stock`);
    if (priceChangedCount) parts.push(`${priceChangedCount} cost`);
    if (configurationChangedCount) parts.push(`${configurationChangedCount} configuration`);
    if (metadataChangedCount) parts.push(`${metadataChangedCount} metadata`);
    if (parts.length) summary = parts.join(' · ');
  }

  return {
    version: 2,
    initial,
    previousProductCount: previous.size,
    currentProductCount: current.size,
    addedCount,
    removedCount,
    quantityChangedCount,
    priceChangedCount,
    configurationChangedCount,
    metadataChangedCount,
    unchangedCount,
    changedCount,
    netSohChange,
    summary,
    classifications: {
      added: addedCount,
      removed: removedCount,
      stock: quantityChangedCount,
      cost: priceChangedCount,
      configuration: configurationChangedCount,
      metadata: metadataChangedCount,
      unchanged: unchangedCount,
    },
    changes,
  };
}


function buildReadinessChecks({ snapshot = {}, delta = {}, revision = 0, previousRevision = 0, scope = 'ALL', duplicate = false, cloudSynced = false } = {}) {
  const productCount = Number(snapshot.rowCount ?? snapshot.rawRows?.length ?? snapshot.preparedRows?.length ?? 0);
  const expectedCurrent = Number(delta.currentProductCount ?? productCount);
  return {
    snapshotCommitted: Boolean(snapshot.importId || snapshot.activeImportId),
    ledgerEntryCreated: revision > 0,
    baselineLinked: revision <= 1 || previousRevision === revision - 1,
    comparisonGenerated: Boolean(delta && typeof delta === 'object' && delta.classifications),
    classificationsGenerated: Boolean(delta?.classifications),
    productTotalsReconciled: expectedCurrent === productCount,
    scopeValid: normalizeScope(scope) !== 'ALL',
    cloudSynced: Boolean(cloudSynced),
    duplicateFree: !duplicate,
  };
}

function readinessStatus(checks = {}) {
  const localChecks = Object.entries(checks).filter(([key]) => key !== 'cloudSynced');
  if (localChecks.some(([, value]) => !value)) return 'incomplete';
  return checks.cloudSynced ? 'ready' : 'syncing';
}

function completedAnalysisIsReady({ entry = {}, checks = {}, cloudSynced = false } = {}) {
  // The deterministic revision analysis is already complete once the delta /
  // classifications have been generated and the product totals reconcile.
  // Do not leave a successfully cloud-acknowledged revision in an indefinite
  // "Preparing analysis" state because a secondary lifecycle check is stale.
  return Boolean(
    cloudSynced &&
    Number(entry?.revision || 0) > 0 &&
    checks?.ledgerEntryCreated &&
    checks?.comparisonGenerated &&
    checks?.classificationsGenerated &&
    checks?.productTotalsReconciled &&
    checks?.scopeValid &&
    checks?.duplicateFree
  );
}

export function buildStorageRevisionHandoff(entry = {}) {
  if (!entry || entry.readiness?.status !== 'ready') return null;
  return {
    contractVersion: 1,
    organisationId: entry.organisationId || '',
    site: entry.site || '',
    siteName: entry.siteName || '',
    revision: Number(entry.revision || 0),
    previousRevision: Math.max(0, Number(entry.revision || 0) - 1),
    snapshotReference: entry.reference || entry.importId || '',
    movementSummary: entry.delta?.classifications || {},
    productChanges: entry.delta?.changes || {},
    classifications: entry.delta?.classifications || {},
    patternsEligible: Number(entry.revision || 0) > 1,
    committedAt: entry.timestamp || entry.createdAt || '',
    readyAt: entry.readiness?.readyAt || '',
    integrityStatus: 'ready_for_transaction_integrity',
  };
}

function broadcastReady(scope, entry) {
  if (typeof window === 'undefined' || !entry) return;
  window.dispatchEvent(new CustomEvent(MC_STORAGE_REVISION_READY_EVENT, { detail: { scope: normalizeScope(scope), entry, handoff: buildStorageRevisionHandoff(entry) } }));
}

function normalizeLegacyReadiness(entry = {}) {
  if (!entry || typeof entry !== 'object') return entry;
  const status = String(entry.readiness?.status || '').trim();
  const phase = String(entry.metadata?.storagePhase || '').trim();

  // Revisions created before Phase I1.3.6 never had a readiness lifecycle.
  // They already contain a committed snapshot delta and classification result,
  // so treating them as permanently "syncing" leaves older sites stuck on
  // Preparing analysis forever. Migrate only those legacy rows to Ready.
  const isLegacyRevision = phase !== 'I1.3.6' && phase !== 'I1.3.8';
  const hasCompletedAnalysis = Boolean(entry.delta?.classifications && Number(entry.revision || 0) > 0);
  if ((!status || status === 'syncing') && isLegacyRevision && hasCompletedAnalysis) {
    const migratedAt = entry.timestamp || entry.createdAt || new Date().toISOString();
    return {
      ...entry,
      readiness: {
        ...(entry.readiness || {}),
        status: 'ready',
        label: 'Ready for analysis',
        downstreamReady: true,
        checks: {
          ...(entry.readiness?.checks || {}),
          legacyRevisionMigrated: true,
        },
        readyAt: entry.readiness?.readyAt || migratedAt,
        error: '',
      },
    };
  }
  return entry;
}

function normalizeEntries(value) {
  const rows = Array.isArray(value) ? value : Array.isArray(value?.entries) ? value.entries : [];
  return rows
    .filter(Boolean)
    .map(normalizeLegacyReadiness)
    .sort((a, b) => (Date.parse(b.timestamp || b.createdAt || '') || 0) - (Date.parse(a.timestamp || a.createdAt || '') || 0))
    .slice(0, MAX_ENTRIES);
}

function compactCloudLedgerEntries(entries = []) {
  // MC497 — canonicalSohState is a full current-SOH checkpoint. Keeping that
  // same map inside every historical revision made the single site ledger grow
  // very quickly and turned every Global Distribution read/write into hundreds
  // of KB. One newest checkpoint is sufficient: older immutable deltas preserve
  // history, while the newest checkpoint is the exact continuity baseline for
  // the next import. Local IndexedDB remains untouched/full fidelity.
  let checkpointKept = false;
  return normalizeEntries(entries).map((entry) => {
    const checkpoint = entry?.metadata?.canonicalSohState;
    if (!checkpoint || typeof checkpoint !== 'object' || Array.isArray(checkpoint) || checkpointKept) {
      if (!checkpointKept && checkpoint && typeof checkpoint === 'object' && !Array.isArray(checkpoint)) checkpointKept = true;
      if (!checkpoint || !entry?.metadata) return entry;
      const metadata = { ...entry.metadata };
      delete metadata.canonicalSohState;
      return { ...entry, metadata };
    }
    checkpointKept = true;
    return entry;
  });
}

function cloudLedgerPayload(scope, revision, entries = []) {
  // The local IndexedDB ledger remains the full 250-entry authority. Firestore
  // is a cross-device replica and must stay below the 1 MiB document ceiling.
  // MC497 also keeps the full canonical SOH checkpoint only on the newest cloud
  // revision; older entries retain their immutable deltas but not repeated state.
  const kept = [];
  for (const entry of compactCloudLedgerEntries(entries)) {
    const candidate = [...kept, entry];
    const raw = JSON.stringify({ version: 1, scope: normalizeScope(scope), revision, entries: candidate });
    if (raw.length > MAX_CLOUD_LEDGER_BYTES && kept.length) break;
    kept.push(entry);
  }
  return JSON.stringify({ version: 1, scope: normalizeScope(scope), revision, entries: kept });
}

function normalizeCloudReadiness(entry = {}) {
  if (!entry || typeof entry !== 'object') return entry;

  const status = String(entry.readiness?.status || '').trim();
  if (status === 'ready') return entry;

  // DS1.5 historical revision backfill:
  // If a revision is being read from Firestore, that read is direct evidence
  // that the revision payload exists in the shared cloud authority. Older site
  // revisions could be stamped `sync_failed` when the second acknowledgement
  // write exceeded the old document contract, even though the immutable delta
  // itself was already present in Firestore. Preserve the original revision
  // number/timestamp/source and repair readiness only when the stored payload is
  // structurally complete. We never manufacture delta rows from summary totals.
  const revision = Number(entry.revision || 0);
  const productCount = Number(entry.productCount ?? entry.delta?.currentProductCount ?? 0);
  const deltaCount = Number(entry.delta?.currentProductCount ?? productCount);
  const hasAnalysis = Boolean(
    revision > 0 &&
    entry.delta &&
    typeof entry.delta === 'object' &&
    entry.delta.classifications &&
    entry.delta.changes &&
    productCount >= 0 &&
    deltaCount === productCount
  );

  if (!hasAnalysis) return entry;

  const readyAt = entry.readiness?.readyAt || entry.timestamp || entry.createdAt || new Date().toISOString();
  return {
    ...entry,
    readiness: {
      ...(entry.readiness || {}),
      status: 'ready',
      label: 'Ready for analysis',
      downstreamReady: true,
      checks: {
        ...(entry.readiness?.checks || {}),
        cloudSynced: true,
        cloudReadConfirmed: true,
        comparisonGenerated: true,
        classificationsGenerated: true,
        productTotalsReconciled: true,
        historicalCloudBackfill: status === 'sync_failed' || undefined,
      },
      readyAt,
      error: '',
    },
  };
}

function parseCloud(raw) {
  if (!raw) return [];
  try {
    return normalizeEntries(JSON.parse(raw)).map(normalizeCloudReadiness);
  } catch (_) {
    return [];
  }
}

function cloudReadinessRepairNeeded(raw, normalized = []) {
  if (!raw || !Array.isArray(normalized) || !normalized.length) return false;
  try {
    const parsed = JSON.parse(raw);
    const original = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.entries) ? parsed.entries : [];
    const originalById = new Map(original.filter(Boolean).map((entry) => [String(entry.id || entry.reference || entry.importId || ''), entry]));
    return normalized.some((entry) => {
      const id = String(entry.id || entry.reference || entry.importId || '');
      const before = originalById.get(id);
      if (!before) return false;
      return String(before?.readiness?.status || '').trim() === 'sync_failed' && String(entry?.readiness?.status || '').trim() === 'ready';
    });
  } catch (_) {
    return false;
  }
}

function broadcast(scope, entries) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MC_STORAGE_LEDGER_EVENT, { detail: { scope: normalizeScope(scope), entries } }));
}


function parseMovementHead(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object' || Number(parsed.version || 0) !== 2) return null;
    return {
      ...parsed,
      scope: normalizeScope(parsed.scope || 'ALL'),
      latestRevision: Math.max(0, Number(parsed.latestRevision || 0)),
      canonicalSohState: parsed.canonicalSohState && typeof parsed.canonicalSohState === 'object' && !Array.isArray(parsed.canonicalSohState)
        ? parsed.canonicalSohState
        : {},
      recentImportIds: Array.isArray(parsed.recentImportIds) ? parsed.recentImportIds.map((value) => String(value || '').trim()).filter(Boolean).slice(0, MAX_ENTRIES) : [],
      v2Revisions: Array.isArray(parsed.v2Revisions)
        ? Array.from(new Set(parsed.v2Revisions.map((value) => Number(value || 0)).filter((value) => value > 0))).sort((a, b) => a - b).slice(-MAX_ENTRIES)
        : [],
      legacy: parsed.legacy && typeof parsed.legacy === 'object'
        ? {
            key: String(parsed.legacy.key || ''),
            latestRevision: Math.max(0, Number(parsed.legacy.latestRevision || 0)),
            oldestRevision: Math.max(0, Number(parsed.legacy.oldestRevision || 0)),
          }
        : { key: '', latestRevision: 0, oldestRevision: 0 },
    };
  } catch (_) {
    return null;
  }
}

function movementHeadPayload(head = {}) {
  return JSON.stringify({
    version: 2,
    scope: normalizeScope(head.scope || 'ALL'),
    latestRevision: Math.max(0, Number(head.latestRevision || 0)),
    latestImportId: String(head.latestImportId || ''),
    latestTimestamp: String(head.latestTimestamp || ''),
    latestEntryId: String(head.latestEntryId || ''),
    canonicalSohState: head.canonicalSohState && typeof head.canonicalSohState === 'object' && !Array.isArray(head.canonicalSohState)
      ? head.canonicalSohState
      : {},
    recentImportIds: Array.isArray(head.recentImportIds) ? head.recentImportIds.slice(0, MAX_ENTRIES) : [],
    v2Revisions: Array.isArray(head.v2Revisions) ? head.v2Revisions.slice(-MAX_ENTRIES) : [],
    legacy: head.legacy || { key: '', latestRevision: 0, oldestRevision: 0 },
    storagePhase: 'MC499',
    updatedAt: head.updatedAt || new Date().toISOString(),
  });
}

function movementRevisionPayload(scope = 'ALL', entry = {}) {
  const metadata = { ...(entry?.metadata || {}) };
  // MC499 — the canonical checkpoint belongs to the small mutable head. Repeating
  // it inside every immutable revision recreates the same growth problem we are
  // removing from the v1 ledger.
  delete metadata.canonicalSohState;
  return JSON.stringify({
    version: 2,
    scope: normalizeScope(scope),
    revision: Math.max(0, Number(entry?.revision || 0)),
    entry: { ...entry, metadata },
  });
}

function parseMovementRevision(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const entry = parsed?.entry && typeof parsed.entry === 'object' ? parsed.entry : parsed;
    return entry && typeof entry === 'object' ? normalizeCloudReadiness(entry) : null;
  } catch (_) {
    return null;
  }
}

function latestRevisionOf(entries = []) {
  return Math.max(0, ...(Array.isArray(entries) ? entries : []).map((entry) => Number(entry?.revision || 0)));
}

function oldestRevisionOf(entries = []) {
  const revisions = (Array.isArray(entries) ? entries : []).map((entry) => Number(entry?.revision || 0)).filter((value) => value > 0);
  return revisions.length ? Math.min(...revisions) : 0;
}

function recentImportIdsFromEntries(entries = []) {
  return normalizeEntries(entries)
    .map((entry) => String(entry?.importId || entry?.reference || '').trim())
    .filter(Boolean)
    .slice(0, MAX_ENTRIES);
}

function buildMovementHeadFromLegacy(scope = 'ALL', entries = []) {
  const normalized = normalizeEntries(entries);
  const latestRevision = latestRevisionOf(normalized);
  const latest = normalized.find((entry) => Number(entry?.revision || 0) === latestRevision) || normalized[0] || null;
  return {
    version: 2,
    scope: normalizeScope(scope),
    latestRevision,
    latestImportId: String(latest?.importId || latest?.reference || ''),
    latestTimestamp: String(latest?.timestamp || latest?.createdAt || ''),
    latestEntryId: String(latest?.id || ''),
    canonicalSohState: Object.fromEntries(latestCanonicalSoh(normalized).entries()),
    recentImportIds: recentImportIdsFromEntries(normalized),
    v2Revisions: [],
    legacy: {
      key: cloudKey(scope),
      latestRevision,
      oldestRevision: oldestRevisionOf(normalized),
    },
    storagePhase: 'MC499',
    updatedAt: new Date().toISOString(),
  };
}

function movementHeadForRevision(scope = 'ALL', previousHead = null, entry = {}) {
  const revision = Math.max(0, Number(entry?.revision || 0));
  const importId = String(entry?.importId || entry?.reference || '').trim();
  const previousImports = Array.isArray(previousHead?.recentImportIds) ? previousHead.recentImportIds : [];
  const previousRevisions = Array.isArray(previousHead?.v2Revisions) ? previousHead.v2Revisions : [];
  return {
    version: 2,
    scope: normalizeScope(scope),
    latestRevision: revision,
    latestImportId: importId,
    latestTimestamp: String(entry?.timestamp || entry?.createdAt || ''),
    latestEntryId: String(entry?.id || ''),
    canonicalSohState: entry?.metadata?.canonicalSohState && typeof entry.metadata.canonicalSohState === 'object'
      ? entry.metadata.canonicalSohState
      : (previousHead?.canonicalSohState || {}),
    recentImportIds: Array.from(new Set([importId, ...previousImports].filter(Boolean))).slice(0, MAX_ENTRIES),
    v2Revisions: Array.from(new Set([...previousRevisions, revision].filter((value) => Number(value || 0) > 0))).map(Number).sort((a, b) => a - b).slice(-MAX_ENTRIES),
    legacy: previousHead?.legacy || { key: cloudKey(scope), latestRevision: 0, oldestRevision: 0 },
    storagePhase: 'MC499',
    updatedAt: new Date().toISOString(),
  };
}

function applyHeadCheckpoint(entries = [], head = null) {
  const normalized = normalizeEntries(entries);
  if (!head || !head.canonicalSohState || typeof head.canonicalSohState !== 'object' || !Object.keys(head.canonicalSohState).length) return normalized;
  const latestRevision = Math.max(0, Number(head.latestRevision || 0));
  let applied = false;
  const rows = normalized.map((entry) => {
    if (applied || Number(entry?.revision || 0) !== latestRevision) return entry;
    applied = true;
    return {
      ...entry,
      metadata: {
        ...(entry?.metadata || {}),
        canonicalSohState: head.canonicalSohState,
      },
    };
  });
  return rows;
}

async function readMovementHead(scope = 'ALL', options = {}) {
  options.assertActive?.();
  options.onStage?.('Loading site revision history');
  let raw = null;
  try {
    raw = await mcGetCloudItem(headCloudKey(scope), { throwOnError: options.importAction === true });
  } catch (error) {
    if (options.importAction) throw error;
  }
  options.assertActive?.();
  return parseMovementHead(raw);
}

async function ensureMovementHeadForImport(scope = 'ALL', options = {}) {
  const existingHead = await readMovementHead(scope, options);
  if (existingHead) return { head: existingHead, legacyEntries: null, created: false };

  // One-time bridge: read the existing v1 ledger once, capture its latest
  // canonical checkpoint and sequence position, then freeze that document as the
  // legacy base. Future imports never rewrite it again.
  options.onStage?.('Loading earlier site history');
  const legacyEntries = await loadLegacyStorageMovementLedger(scope, { ...options, freeze: true, preserveLegacyAuthority: true });
  options.assertActive?.();
  if (!legacyEntries.length) {
    return {
      head: {
        version: 2,
        scope: normalizeScope(scope),
        latestRevision: 0,
        latestImportId: '',
        latestTimestamp: '',
        latestEntryId: '',
        canonicalSohState: {},
        recentImportIds: [],
        v2Revisions: [],
        legacy: { key: cloudKey(scope), latestRevision: 0, oldestRevision: 0 },
        storagePhase: 'MC499',
        updatedAt: new Date().toISOString(),
      },
      legacyEntries,
      created: false,
    };
  }

  const migratedHead = buildMovementHeadFromLegacy(scope, legacyEntries);
  try {
    options.assertActive?.();
    options.onStage?.('Confirming earlier site history');
    const ok = await mcSetCloudItemImmediate(headCloudKey(scope), movementHeadPayload(migratedHead), {
      trustServerCommit: true,
      throwOnError: options.importAction === true,
    });
    options.assertActive?.();
    if (ok) {
      console.info('[MC499 STORAGE] v1 ledger frozen behind compact v2 head', {
        scope: normalizeScope(scope),
        latestRevision: migratedHead.latestRevision,
        legacyEntries: legacyEntries.length,
      });
      return { head: migratedHead, legacyEntries, created: true };
    }
  } catch (error) {
    console.warn('[MC499 STORAGE] could not create v2 movement head', { scope: normalizeScope(scope), error });
    if (options.importAction) throw error;
  }
  if (options.importAction) throw new Error('The existing site revision head could not be confirmed. Inventory history was preserved; retry this site.');
  return { head: migratedHead, legacyEntries, created: false };
}

async function loadStorageMovementImportAuthority(scope = 'ALL', options = {}) {
  const [cachedEntries, ensured] = await Promise.all([
    readLedgerCache(() => idbGetJson(localKey(scope), []), [], options),
    ensureMovementHeadForImport(scope, options),
  ]);
  options.assertActive?.();
  const localEntries = normalizeEntries(cachedEntries);
  const head = ensured.head;
  const latestRevision = Math.max(0, Number(head?.latestRevision || 0));

  // A warm browser keeps the full 250-entry IndexedDB ledger. If it is current,
  // use it directly for delta analysis with no cloud history read. If it is stale
  // or empty, the head's canonical SOH checkpoint is sufficient to calculate the
  // next revision correctly; full historical deltas stay asleep until a history/
  // intelligence screen explicitly asks for them.
  const localCurrent = latestRevisionOf(localEntries) === latestRevision;
  const comparisonEntries = localCurrent && localEntries.length
    ? applyHeadCheckpoint(localEntries, head)
    : latestRevision > 0
      ? [{
          id: `mc499-head:${normalizeScope(scope)}:r${latestRevision}`,
          revision: latestRevision,
          importId: head?.latestImportId || '',
          reference: head?.latestImportId || '',
          timestamp: head?.latestTimestamp || '',
          createdAt: head?.latestTimestamp || '',
          metadata: { canonicalSohState: head?.canonicalSohState || {}, storagePhase: 'MC499-head-authority' },
        }]
      : [];

  return {
    head,
    localEntries,
    comparisonEntries,
    latestRevision,
    legacyEntries: ensured.legacyEntries,
  };
}

async function persistMovementRevisionV2(scope = 'ALL', entry = {}, previousHead = null, options = {}) {
  options.assertActive?.();
  const revision = Math.max(0, Number(entry?.revision || 0));
  if (!revision) return { ok: false, reason: 'missing-revision' };

  // Revision first, pointer second. A head must never advertise a revision whose
  // immutable payload failed to commit.
  options.onStage?.(`Saving inventory revision R${revision}`);
  const revisionOk = await mcSetCloudItemImmediate(
    revisionCloudKey(scope, revision),
    movementRevisionPayload(scope, entry),
    { trustServerCommit: true, throwOnError: options.importAction === true },
  );
  options.assertActive?.();
  if (!revisionOk) return { ok: false, revisionOk: false, headOk: false, reason: 'revision-write-failed' };

  const nextHead = movementHeadForRevision(scope, previousHead, entry);
  options.onStage?.('Confirming site revision');
  const headOk = await mcSetCloudItemImmediate(
    headCloudKey(scope),
    movementHeadPayload(nextHead),
    { trustServerCommit: true, throwOnError: options.importAction === true },
  );
  options.assertActive?.();
  return { ok: Boolean(revisionOk && headOk), revisionOk: Boolean(revisionOk), headOk: Boolean(headOk), head: nextHead };
}

export async function loadStorageMovementLedger(scope = 'ALL') {
  const head = await readMovementHead(scope);
  if (!head) return loadLegacyStorageMovementLedger(scope);

  let local = normalizeEntries(await idbGetJson(localKey(scope), []).catch(() => []));
  const latestHeadRevision = Math.max(0, Number(head.latestRevision || 0));
  const legacyMaxRevision = Math.max(0, Number(head?.legacy?.latestRevision || 0));
  const localRevisionSet = new Set(local.map((entry) => Number(entry?.revision || 0)).filter((value) => value > 0));
  const legacyOldestRevision = Math.max(0, Number(head?.legacy?.oldestRevision || 0));
  const localLegacyRevisions = local
    .map((entry) => Number(entry?.revision || 0))
    .filter((revision) => revision > 0 && revision <= legacyMaxRevision);
  const hasLegacyCoverage = legacyMaxRevision <= 0 || (
    localLegacyRevisions.length > 0 &&
    (legacyOldestRevision <= 0 || Math.min(...localLegacyRevisions) <= legacyOldestRevision)
  );
  const hasAllV2Revisions = (Array.isArray(head.v2Revisions) ? head.v2Revisions : []).every((revision) => localRevisionSet.has(Number(revision || 0)));
  const localHistoryCurrent = latestRevisionOf(local) === latestHeadRevision && hasLegacyCoverage && hasAllV2Revisions;
  if (localHistoryCurrent) {
    const current = applyHeadCheckpoint(local, head);
    await idbSetJson(localKey(scope), current, { type: 'storage-movement-ledger', scope: normalizeScope(scope), storagePhase: 'MC499', source: 'v2-head-cache' }).catch(() => {});
    return current;
  }

  // Rehydrate only when the local cache is stale/new. Existing v1 cloud history
  // is treated as a frozen legacy base; every MC499+ revision is an independent
  // immutable document referenced by the compact head.
  let combined = [...local];
  const legacyMax = Math.max(0, Number(head?.legacy?.latestRevision || 0));
  const hasLegacyLocal = legacyMax > 0 && combined.some((entry) => Number(entry?.revision || 0) > 0 && Number(entry?.revision || 0) <= legacyMax);
  if (legacyMax > 0 && !hasLegacyLocal) {
    const legacy = await loadLegacyStorageMovementLedger(scope, { freeze: true }).catch(() => []);
    combined = normalizeEntries([...combined, ...legacy]);
  }

  const byRevision = new Map(combined.filter(Boolean).map((entry) => [Number(entry?.revision || 0), entry]));
  for (const revision of Array.isArray(head.v2Revisions) ? head.v2Revisions : []) {
    const numericRevision = Number(revision || 0);
    if (!numericRevision || byRevision.has(numericRevision)) continue;
    try {
      const raw = await mcGetCloudItem(revisionCloudKey(scope, numericRevision));
      const entry = parseMovementRevision(raw);
      if (entry) byRevision.set(numericRevision, entry);
    } catch (error) {
      console.warn('[MC499 STORAGE] revision hydration failed', { scope: normalizeScope(scope), revision: numericRevision, error });
    }
  }

  combined = applyHeadCheckpoint(normalizeEntries(Array.from(byRevision.values())), head);
  await idbSetJson(localKey(scope), combined, { type: 'storage-movement-ledger', scope: normalizeScope(scope), storagePhase: 'MC499', source: 'v2-rehydration' }).catch(() => {});
  return combined;
}

async function loadLegacyStorageMovementLedger(scope = 'ALL', options = {}) {
  // Firestore is the canonical revision authority. A browser's IndexedDB history
  // is only a cache/offline fallback and must never be merged into the shared
  // sequence (that previously allowed localhost and deployed to invent R-numbers).
  //
  // DS1.15: mcGetCloudItem can return an empty string when a scoped cloud document
  // does not exist. An empty string is NOT a canonical empty ledger; treating it
  // as one masked the browser's existing site history and made District Six look
  // like it had 0 revisions even while IndexedDB still held R1..R58+.
  let cloudRaw = null;
  try {
    cloudRaw = await mcGetCloudItem(cloudKey(scope), { throwOnError: options.importAction === true });
  } catch (error) {
    if (options.importAction) throw error;
  }
  options.assertActive?.();
  const hasCloudPayload = cloudRaw !== null && cloudRaw !== undefined && String(cloudRaw).trim() !== '';
  if (hasCloudPayload) {
    const cloud = parseCloud(cloudRaw);
    await writeLedgerCache(localKey(scope), cloud, { type: 'storage-movement-ledger', scope: normalizeScope(scope), storagePhase: 'DS1.5', source: 'cloud-authority' }, options);

    // DS1.5: persist repaired readiness exactly once when Firestore itself proves
    // that an older `sync_failed` revision is present and analytically complete.
    // This is a metadata/readiness backfill only: revision IDs, sequence, source,
    // timestamps and delta rows are left untouched. If a historical revision is
    // genuinely absent or incomplete, it remains unavailable rather than being
    // reconstructed from totals.
    if (options?.freeze !== true && cloudReadinessRepairNeeded(cloudRaw, cloud)) {
      const revision = Math.max(0, ...cloud.map((entry) => Number(entry.revision || 0)));
      try { await mcSetCloudItemImmediate(cloudKey(scope), cloudLedgerPayload(scope, revision, cloud)); } catch (_) {}
    }
    return cloud;
  }

  // Cloud key is genuinely absent/blank. Recover the existing site-local ledger
  // rather than interpreting the missing document as an authoritative empty list.
  // If a warm browser still holds historical revisions, promote that exact bounded
  // history to Firestore once so localhost/deployed converge from this point on.
  let local = normalizeEntries(await readLedgerCache(() => idbGetJson(localKey(scope), []), [], options));
  if (options.importAction) local = local.filter((entry) => !['sync_failed', 'syncing', 'incomplete'].includes(entry?.readiness?.status));

  // DS1.17: compatibility bridge for ledgers created before org-scoped browser
  // keys were introduced. Those histories used keys such as
  // `mc-storage-movement-ledger:v1:district_six`. They are valid historical
  // evidence for the same normalized site, but a new laptop correctly will not
  // have them. When the cloud authority is missing, a browser that DOES still
  // possess the legacy site ledger may promote that exact history once. We do
  // not merge histories, borrow ALL-site data, or renumber revisions.
  if (!local.length && normalizeScope(scope) !== 'ALL') {
    try {
      const wantedScope = normalizeScope(scope);
      const allRows = await readLedgerCache(() => idbListItems(), [], options);
      const legacyCandidates = (Array.isArray(allRows) ? allRows : [])
        .filter((row) => {
          const key = String(row?.key || '');
          if (!key.startsWith(`${KEY_PREFIX}:`)) return false;
          const suffix = key.slice(`${KEY_PREFIX}:`.length);
          // Current org-scoped cache was already checked above. For historical
          // keys, compare only the final scope segment after an optional `::`.
          const candidateScope = normalizeScope(suffix.includes('::') ? suffix.split('::').pop() : suffix);
          return candidateScope === wantedScope && candidateScope !== 'ALL';
        })
        .map((row) => {
          let entries = [];
          try { entries = normalizeEntries(JSON.parse(String(row?.value || ''))); } catch (_) {}
          if (options.importAction) entries = entries.filter((entry) => !['sync_failed', 'syncing', 'incomplete'].includes(entry?.readiness?.status));
          return { key: String(row?.key || ''), entries };
        })
        .filter((candidate) => candidate.entries.length)
        .sort((a, b) => {
          const aRevision = Math.max(0, ...a.entries.map((entry) => Number(entry?.revision || 0)));
          const bRevision = Math.max(0, ...b.entries.map((entry) => Number(entry?.revision || 0)));
          return bRevision - aRevision || b.entries.length - a.entries.length;
        });

      if (legacyCandidates.length) {
        const recovered = legacyCandidates[0];
        local = recovered.entries;
        await writeLedgerCache(localKey(scope), local, {
          type: 'storage-movement-ledger',
          scope: wantedScope,
          storagePhase: 'DS1.17',
          source: 'legacy-local-recovery',
          recoveredFrom: recovered.key,
        }, options);
        console.info('[STORAGE AUTHORITY] recovered legacy site ledger into current org cache', {
          scope: wantedScope,
          recoveredFrom: recovered.key,
          revisions: local.length,
          latestRevision: Math.max(0, ...local.map((entry) => Number(entry?.revision || 0))),
        });
      }
    } catch (error) {
      console.warn('[STORAGE AUTHORITY] legacy site ledger recovery probe failed', { scope: normalizeScope(scope), error });
    }
  }

  // DS1.16 diagnostic: if the current org/scope cache is empty, inspect every
  // Storage Movement Ledger row still present in this browser's IndexedDB.
  // This is read-only and deliberately does NOT promote or merge anything. It
  // tells us whether the historical R1..R58+ chain exists under an older org id
  // or scope key, which the cloud-key probe cannot see.
  if (!local.length && !options.importAction && typeof console !== 'undefined') {
    try {
      const allRows = await idbListItems();
      const candidates = (Array.isArray(allRows) ? allRows : [])
        .filter((row) => String(row?.key || '').startsWith(`${KEY_PREFIX}:`))
        .map((row) => {
          let entries = [];
          let parseError = '';
          try {
            const parsed = JSON.parse(String(row?.value || ''));
            entries = normalizeEntries(parsed);
          } catch (error) {
            parseError = String(error?.message || error);
          }
          return {
            key: String(row?.key || ''),
            count: entries.length,
            revisions: entries.slice(0, 12).map((entry) => Number(entry?.revision || 0)).join(','),
            latestRevision: entries.length ? Math.max(...entries.map((entry) => Number(entry?.revision || 0))) : 0,
            bytes: Number(row?.bytes || String(row?.value || '').length * 2 || 0),
            updatedAt: row?.updatedAt || '',
            parseError,
          };
        })
        .sort((a, b) => b.latestRevision - a.latestRevision || b.count - a.count);

      const syncCandidates = (Array.isArray(allRows) ? allRows : [])
        .filter((row) => String(row?.key || '').startsWith(`${SYNC_KEY_PREFIX}:`))
        .map((row) => {
          let entries = [];
          let parseError = '';
          try {
            const parsed = JSON.parse(String(row?.value || ''));
            entries = normalizeSyncEntries(parsed);
          } catch (error) {
            parseError = String(error?.message || error);
          }
          return {
            key: String(row?.key || ''),
            count: entries.length,
            bytes: Number(row?.bytes || String(row?.value || '').length * 2 || 0),
            updatedAt: row?.updatedAt || '',
            parseError,
          };
        })
        .sort((a, b) => b.count - a.count);

      const payload = {
        requestedScope: normalizeScope(scope),
        currentOrgId: orgId(),
        expectedLocalKey: localKey(scope),
        expectedSyncLocalKey: syncLocalKey(scope),
        ledgerCandidates: candidates,
        syncCandidates,
      };
      if (typeof window !== 'undefined') window.__MC_STORAGE_LOCAL_AUTHORITY_DEBUG__ = payload;
      console.groupCollapsed(`[STORAGE LOCAL DEBUG] ${normalizeScope(scope)} · ${candidates.length} IndexedDB ledger key(s)`);
      console.log('Expected current local key', payload.expectedLocalKey);
      console.log('Current org id', payload.currentOrgId);
      console.table(candidates);
      console.log('Sync candidates');
      console.table(syncCandidates);
      console.log('Full payload: window.__MC_STORAGE_LOCAL_AUTHORITY_DEBUG__');
      console.groupEnd();
    } catch (error) {
      console.warn('[STORAGE LOCAL DEBUG] could not enumerate IndexedDB storage history', error);
    }
  }

  if (local.length && (options?.freeze !== true || options.preserveLegacyAuthority === true)) {
    const revision = Math.max(0, ...local.map((entry) => Number(entry.revision || 0)));
    try {
      options.assertActive?.();
      const promoted = await mcSetCloudItemImmediate(cloudKey(scope), cloudLedgerPayload(scope, revision, local), { trustServerCommit: options.importAction === true });
      options.assertActive?.();
      if (!promoted && options.importAction) throw new Error('Earlier site history could not be saved. The revision sequence was left unchanged.');
      if (promoted) {
        console.info('[STORAGE AUTHORITY] promoted missing site ledger from IndexedDB to Firestore', {
          scope: normalizeScope(scope),
          revisions: local.length,
          latestRevision: revision,
        });
      }
    } catch (error) {
      if (options.importAction) throw error;
      console.warn('[STORAGE AUTHORITY] site ledger promotion failed; continuing from local cache', { scope: normalizeScope(scope), error });
    }
  }
  return local;
}


function normalizeSyncEntries(value) {
  const rows = Array.isArray(value) ? value : Array.isArray(value?.entries) ? value.entries : [];
  return rows
    .filter(Boolean)
    .sort((a, b) => (Date.parse(b.timestamp || b.createdAt || '') || 0) - (Date.parse(a.timestamp || a.createdAt || '') || 0))
    .slice(0, MAX_SYNC_ENTRIES);
}

function parseSyncCloud(raw) {
  if (!raw) return [];
  try { return normalizeSyncEntries(JSON.parse(raw)); } catch (_) { return []; }
}

function broadcastSync(scope, entries) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('mc-storage-sync-activity-changed', { detail: { scope: normalizeScope(scope), entries } }));
}

export async function loadStorageSyncActivity(scope = 'ALL', options = {}) {
  // Sync Activity follows the same authority rule as Storage Movement History:
  // cloud is canonical; IndexedDB is a cache only. Never union browser-specific
  // histories into the organisation/site audit trail.
  // DS1.15 mirrors the ledger fix: blank cloud values mean missing, not an
  // authoritative zero-entry history.
  options.assertActive?.();
  options.onStage?.('Loading site sync history');
  let cloudRaw = null;
  try {
    cloudRaw = await mcGetCloudItem(syncCloudKey(scope), { throwOnError: options.importAction === true });
  } catch (error) {
    if (options.importAction) throw error;
  }
  options.assertActive?.();
  const hasCloudPayload = cloudRaw !== null && cloudRaw !== undefined && String(cloudRaw).trim() !== '';
  if (hasCloudPayload) {
    const cloud = parseSyncCloud(cloudRaw);
    await writeLedgerCache(syncLocalKey(scope), cloud, { type: 'storage-sync-activity', scope: normalizeScope(scope), storagePhase: 'I1.3.8', source: 'cloud-authority' }, options);
    return cloud;
  }
  const local = normalizeSyncEntries(await readLedgerCache(() => idbGetJson(syncLocalKey(scope), []), [], options));
  // An import saves the combined history once below. Do not first upload the
  // old cache and perform an additional read-after-write verification.
  if (local.length && !options.importAction) {
    try {
      const promoted = await mcSetCloudItemImmediate(
        syncCloudKey(scope),
        JSON.stringify({ version: 1, scope: normalizeScope(scope), entries: local }),
      );
      if (promoted) {
        console.info('[STORAGE AUTHORITY] promoted missing site sync history from IndexedDB to Firestore', {
          scope: normalizeScope(scope),
          entries: local.length,
        });
      }
    } catch (error) {
      console.warn('[STORAGE AUTHORITY] site sync promotion failed; continuing from local cache', { scope: normalizeScope(scope), error });
    }
  }
  return local;
}

async function recordStorageSyncActivity(scope = 'ALL', snapshot = {}, actor = {}, delta = {}, revision = 0, options = {}) {
  options.assertActive?.();
  const importId = String(snapshot.importId || snapshot.activeImportId || '').trim();
  if (!importId) return { ok: false, reason: 'missing-import-id' };
  const entries = await loadStorageSyncActivity(scope, options);
  options.assertActive?.();
  const existing = entries.find((entry) => entry.importId === importId || entry.reference === importId);
  if (existing && options.awaitCloud !== true) return { ok: true, duplicate: true, entry: existing, entries };
  const timestamp = snapshot.uploadedAt || snapshot.updatedAt || new Date().toISOString();
  const changed = Boolean(delta?.initial || Number(delta?.changedCount || 0) > 0);
  const entry = existing || {
    id: `sync:${normalizeScope(scope)}:${importId}`,
    timestamp,
    createdAt: timestamp,
    organisationId: orgId(),
    site: normalizeScope(scope),
    siteName: snapshot.siteName || snapshot.config?.storeName || '',
    syncType: snapshot.sourceType === 'pos' ? 'pos_sync' : 'csv_import',
    syncLabel: snapshot.sourceType === 'pos' ? 'POS Sync' : 'CSV Import',
    source: snapshot.fileName || snapshot.source || (snapshot.sourceType === 'pos' ? 'POS Sync' : 'CSV Upload'),
    reference: importId,
    importId,
    createdBy: actor.name || actor.displayName || actor.email || 'Morpheus operator',
    createdById: actor.id || actor.profileId || actor.email || '',
    productCount: Number(snapshot.rowCount ?? snapshot.rawRows?.length ?? 0),
    outcome: changed ? 'revision_created' : 'no_changes',
    outcomeLabel: changed ? `Revision R${revision} created` : 'No inventory changes',
    revision: changed ? Number(revision || 0) : 0,
    changedCount: Number(delta?.changedCount || 0),
    classifications: delta?.classifications || {},
    metadata: { storagePhase: 'I1.3.7', fingerprint: snapshot.importFingerprint || '' },
  };
  const next = existing ? entries : normalizeSyncEntries([entry, ...entries]);
  if (options.awaitCloud !== true) {
    await writeLedgerCache(syncLocalKey(scope), next, { type: 'storage-sync-activity', scope: normalizeScope(scope), storagePhase: 'I1.3.7' }, options);
    broadcastSync(scope, next);
  }
  options.assertActive?.();
  options.onStage?.('Saving site sync history');
  const cloudTask = Promise.resolve(mcSetCloudItemImmediate(
    syncCloudKey(scope),
    JSON.stringify({ version: 1, scope: normalizeScope(scope), entries: next }),
    { trustServerCommit: options?.trustServerCommit === true, throwOnError: options.importAction === true },
  )).then((cloudOk) => ({ cloudOk: Boolean(cloudOk), error: null })).catch((error) => {
    console.warn('Storage sync activity cloud replication failed.', error);
    return { cloudOk: false, error };
  });
  if (options?.awaitCloud === true) {
    const { cloudOk, error } = await cloudTask;
    options.assertActive?.();
    if (cloudOk) {
      await writeLedgerCache(syncLocalKey(scope), next, { type: 'storage-sync-activity', scope: normalizeScope(scope), storagePhase: 'I1.3.7' }, options);
      broadcastSync(scope, next);
    }
    return { ok: Boolean(cloudOk), cloudOk: Boolean(cloudOk), error, entry, entries: cloudOk ? next : entries };
  }
  return { ok: true, entry, entries: next, cloudPending: true };
}

export function subscribeStorageSyncActivity(scope = 'ALL', callback = () => {}) {
  return mcSubscribeCloudItem(syncCloudKey(scope), async (raw) => {
    const cloud = parseSyncCloud(raw);
    if (!cloud.length) return;
    await idbSetJson(syncLocalKey(scope), cloud, { type: 'storage-sync-activity', scope: normalizeScope(scope), storagePhase: 'I1.3.7', source: 'cloud' }).catch(() => {});
    callback(cloud);
    broadcastSync(scope, cloud);
  });
}

export async function recordInventoryImportMovement(scope = 'ALL', snapshot = {}, actor = {}, previousSnapshot = null, options = {}) {
  const operationOrgId = orgId();
  const assertActive = () => {
    if (orgId() !== operationOrgId) throw new Error('The organisation changed before the inventory revision finished.');
    options.assertActive?.();
  };
  const actionOptions = { ...options, importAction: true, assertActive };
  assertActive();
  const importId = String(snapshot.importId || snapshot.activeImportId || '').trim();
  if (!importId) return { ok: false, reason: 'missing-import-id' };

  // MC499 — imports now consult a compact per-site head instead of downloading
  // the entire growing movement ledger. A warm browser uses its local full
  // history; a new/stale browser uses the head's canonical SOH checkpoint.
  const authority = await loadStorageMovementImportAuthority(scope, actionOptions);
  assertActive();
  const head = authority.head;
  const entries = authority.comparisonEntries;
  const localEntries = authority.localEntries;
  const headDuplicate = Array.isArray(head?.recentImportIds) && head.recentImportIds.includes(importId);
  const localDuplicate = localEntries.find((entry) => entry.reference === importId || entry.importId === importId);
  // A cache row can describe a failed/pending write. Only the canonical head
  // proves that this import's revision was committed on a previous attempt.
  if (headDuplicate) {
    const existing = localDuplicate || localEntries.find((entry) => String(entry?.importId || entry?.reference || '') === importId) || null;
    return { ok: true, duplicate: true, entry: existing, entries: localEntries.length ? localEntries : undefined };
  }
  const priorLocalEntries = localEntries.filter((entry) => entry.importId !== importId && entry.reference !== importId);

  const timestamp = snapshot.uploadedAt || snapshot.updatedAt || new Date().toISOString();
  const delta = buildRevisionDelta(previousSnapshot, snapshot, entries);
  const hasInventoryChange = Boolean(delta.initial || Number(delta.changedCount || 0) > 0);
  const previousRevision = Math.max(0, Number(authority.latestRevision || 0));
  const revision = hasInventoryChange ? previousRevision + 1 : previousRevision;

  // A synchronization and a storage revision are deliberately different events.
  // Every successful CSV/POS synchronization is recorded, but a new immutable
  // revision is created only when the inventory state actually changed.
  const syncResult = await recordStorageSyncActivity(scope, snapshot, actor, delta, revision, {
    ...actionOptions,
    awaitCloud: options?.awaitCloud === true,
    trustServerCommit: options?.awaitCloud === true,
    assertActive,
  });
  assertActive();
  if (options.awaitCloud === true && !syncResult?.ok) {
    return { ok: false, reason: 'sync-activity-not-confirmed', error: syncResult?.error || null, syncEntries: syncResult?.entries };
  }
  if (!hasInventoryChange) {
    return {
      ok: true, noChange: true, revisionCreated: false,
      entry: null, entries: localEntries.length ? localEntries : undefined, delta, syncEntry: syncResult.entry, syncEntries: syncResult.entries,
    };
  }

  const canonicalState = canonicalSohCheckpoint(entries, snapshot);
  const entry = {
    id: `movement:${normalizeScope(scope)}:${importId}`,
    timestamp,
    createdAt: timestamp,
    organisationId: orgId(),
    site: normalizeScope(scope),
    siteName: snapshot.siteName || snapshot.config?.storeName || '',
    revision,
    movementType: 'inventory_import',
    movementLabel: 'Inventory Import',
    source: snapshot.fileName || snapshot.source || 'CSV Upload',
    reference: importId,
    importId,
    createdBy: actor.name || actor.displayName || actor.email || 'Morpheus operator',
    createdById: actor.id || actor.profileId || actor.email || '',
    productCount: Number(snapshot.rowCount ?? snapshot.rawRows?.length ?? 0),
    changedCount: delta.changedCount,
    delta,
    metadata: {
      columnCount: Number(snapshot.columnCount ?? snapshot.parsedHeaders?.length ?? 0),
      fingerprint: snapshot.importFingerprint || '',
      storagePhase: 'MC499',
      baselineImportId: String(previousSnapshot?.activeImportId || previousSnapshot?.importId || ''),
      canonicalSohState: canonicalState,
    },
  };

  if (process.env.NODE_ENV !== 'production') {
    const stockChanges = Array.isArray(delta?.changes?.stock) ? delta.changes.stock : [];
    console.info('[STORAGE REVISION CONTINUITY]', {
      scope: normalizeScope(scope),
      importId,
      revision,
      priorCanonicalSkuCount: latestCanonicalSoh(entries).size,
      checkpointSkuCount: Object.keys(canonicalState).length,
      storagePhase: 'MC499',
      stockChanges: stockChanges.map((item) => {
        const soh = (Array.isArray(item?.fields) ? item.fields : []).find((field) => String(field?.field || '').toLowerCase() === 'soh');
        return { key: item?.key || '', sku: item?.sku || '', product: item?.product || '', before: soh?.before, after: soh?.after, difference: soh?.difference };
      }),
    });
  }

  const initialChecks = buildReadinessChecks({ snapshot, delta, revision, previousRevision, scope, cloudSynced: false });
  entry.readiness = {
    status: readinessStatus(initialChecks),
    label: 'Syncing revision',
    downstreamReady: false,
    checks: initialChecks,
    startedAt: new Date().toISOString(),
    readyAt: '',
    error: '',
  };
  const next = normalizeEntries([entry, ...priorLocalEntries]);
  await writeLedgerCache(localKey(scope), next, { type: 'storage-movement-ledger', scope: normalizeScope(scope), revision, storagePhase: 'MC499' }, actionOptions);
  assertActive();
  broadcast(scope, next);

  const readyChecks = buildReadinessChecks({ snapshot, delta, revision, previousRevision, scope, cloudSynced: true });
  const readyStatus = completedAnalysisIsReady({ entry, checks: readyChecks, cloudSynced: true })
    ? 'ready'
    : readinessStatus(readyChecks);
  const readyEntry = {
    ...entry,
    readiness: {
      ...entry.readiness,
      status: readyStatus,
      label: readyStatus === 'ready' ? 'Ready for analysis' : 'Preparing analysis',
      downstreamReady: readyStatus === 'ready',
      checks: readyChecks,
      readyAt: readyStatus === 'ready' ? new Date().toISOString() : '',
      error: '',
    },
  };
  const readyNext = normalizeEntries([readyEntry, ...priorLocalEntries]);

  const persistReadyRevision = async () => {
    assertActive();
    const persisted = await persistMovementRevisionV2(scope, readyEntry, head, actionOptions);
    if (!persisted?.ok) {
      throw new Error(persisted?.reason || (persisted?.revisionOk ? 'Movement head write failed.' : 'Movement revision write failed.'));
    }
    await writeLedgerCache(localKey(scope), readyNext, { type: 'storage-movement-ledger', scope: normalizeScope(scope), revision, storagePhase: 'MC499' }, actionOptions);
    assertActive();
    broadcast(scope, readyNext);
    if (readyStatus === 'ready') broadcastReady(scope, readyEntry);
    return persisted;
  };

  const markSyncFailed = async (error) => {
    const failedEntry = {
      ...entry,
      readiness: {
        ...entry.readiness,
        status: 'sync_failed',
        label: 'Revision sync failed',
        downstreamReady: false,
        error: String(error?.message || error || 'Cloud replication failed'),
      },
    };
    const failed = normalizeEntries([failedEntry, ...priorLocalEntries]);
    // An expired action may finish later, but must not repaint another import
    // or write its old failure into a newly selected organisation's cache.
    try { assertActive(); } catch (_) { return { failedEntry, failed }; }
    await writeLedgerCache(localKey(scope), failed, { type: 'storage-movement-ledger', scope: normalizeScope(scope), revision, storagePhase: 'MC499' }, actionOptions);
    broadcast(scope, failed);
    return { failedEntry, failed };
  };

  if (options?.awaitCloud === true) {
    try {
      await persistReadyRevision();
      return {
        ok: true,
        localOk: true,
        cloudOk: true,
        cloudPending: false,
        revisionCreated: true,
        entry: readyEntry,
        entries: readyNext,
        readiness: readyEntry.readiness,
        syncEntry: syncResult.entry,
        syncEntries: syncResult.entries,
      };
    } catch (error) {
      console.warn('Storage movement v2 replication failed.', error);
      const failedResult = await markSyncFailed(error);
      return {
        ok: false,
        localOk: true,
        cloudOk: false,
        cloudPending: false,
        revisionCreated: true,
        entry: failedResult.failedEntry,
        entries: failedResult.failed,
        readiness: failedResult.failedEntry.readiness,
        syncEntry: syncResult.entry,
        syncEntries: syncResult.entries,
        reason: 'cloud-replication-not-confirmed',
        error,
      };
    }
  }

  persistReadyRevision().catch(async (error) => {
    console.warn('Storage movement v2 background replication failed.', error);
    await markSyncFailed(error);
  });

  return {
    ok: true,
    localOk: true,
    cloudPending: true,
    revisionCreated: true,
    entry,
    entries: next,
    readiness: entry.readiness,
    syncEntry: syncResult.entry,
    syncEntries: syncResult.entries,
  };
}

export function subscribeStorageMovementLedger(scope = 'ALL', callback = () => {}) {
  const key = cloudKey(scope);
  return mcSubscribeCloudItem(key, async (raw) => {
    const cloud = parseCloud(raw);
    if (!cloud.length) return;
    await idbSetJson(localKey(scope), cloud, { type: 'storage-movement-ledger', scope: normalizeScope(scope), storagePhase: 'I1.3.3', source: 'cloud' }).catch(() => {});
    callback(cloud);
    broadcast(scope, cloud);
  });
}
